'use client';

import React, { useState, useCallback } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { generateBookingRef } from '@/lib/pricing/pricingEngine';
import { IS_SANDBOX, SANDBOX_WARNING, PAYMENT_PROVIDER_INFO } from '@/lib/payments/nepal-payments';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

// ── Demo data ─────────────────────────────────────────────────────────────────
const NEPAL_CITIES = [
  'Kathmandu', 'Pokhara', 'Chitwan / Bharatpur', 'Butwal', 'Biratnagar',
  'Dharan', 'Hetauda', 'Birgunj', 'Nepalgunj', 'Dhangadhi',
  'Janakpur', 'Lumbini / Bhairahawa', 'Mustang / Jomsom', 'Ilam', 'Taplejung',
];

interface BusTrip {
  id: string;
  operator: string;
  busType: string;
  busNumber: string;
  from: string;
  to: string;
  departureISO: string;
  arrivalISO: string;
  durationMinutes: number;
  totalSeats: number;
  availableSeats: number;
  baseFare: number;
  tax: number;
  serviceFee: number;
  totalFare: number;
  amenities: string[];
  isDemo: boolean;
  demoLabel: string;
}

function generateDemoBusTrips(from: string, to: string, dateStr: string, passengers: number): BusTrip[] {
  if (!from || !to || !dateStr || from === to) return [];
  const base = new Date(`${dateStr}T06:00:00+05:45`);
  const operators = [
    { name: 'Greenline Travels', type: 'Tourist Bus', fare: 1200, duration: 420, amenities: ['AC', 'WiFi', 'Reclining Seats', 'Snacks'] },
    { name: 'Sajha Yatayat', type: 'Deluxe Bus', fare: 800, duration: 450, amenities: ['AC', 'Reclining Seats'] },
    { name: 'Himalayan Express', type: 'Super Deluxe', fare: 1000, duration: 430, amenities: ['AC', 'WiFi', 'Charging Port'] },
    { name: 'Nepal Yatayat', type: 'Local Bus', fare: 500, duration: 480, amenities: ['Standard Seating'] },
  ];
  return operators.map((op, i) => {
    const dep = new Date(base.getTime() + i * 90 * 60000);
    const arr = new Date(dep.getTime() + op.duration * 60000);
    const baseFare = op.fare * passengers;
    const tax = Math.round(baseFare * 0.13);
    const serviceFee = Math.round(baseFare * 0.02);
    return {
      id: `bus-${i + 1}-${dateStr}`,
      operator: op.name,
      busType: op.type,
      busNumber: `BA ${1000 + i * 111} ${String.fromCharCode(65 + i)}`,
      from,
      to,
      departureISO: dep.toISOString(),
      arrivalISO: arr.toISOString(),
      durationMinutes: op.duration,
      totalSeats: 40,
      availableSeats: Math.max(1, 40 - Math.floor(Math.random() * 30)),
      baseFare,
      tax,
      serviceFee,
      totalFare: baseFare + tax + serviceFee,
      amenities: op.amenities,
      isDemo: true,
      demoLabel: '⚠️ Demo — Estimated schedule. Book via operator for confirmed ticket.',
    };
  });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-NP', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kathmandu' });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-NP', { day: 'numeric', month: 'short', timeZone: 'Asia/Kathmandu' });
}

function formatDuration(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function isNextDay(depISO: string, arrISO: string): boolean {
  const dep = new Date(depISO).toLocaleDateString('en-CA', { timeZone: 'Asia/Kathmandu' });
  const arr = new Date(arrISO).toLocaleDateString('en-CA', { timeZone: 'Asia/Kathmandu' });
  return arr > dep;
}

const PAYMENT_OPTIONS = ['esewa', 'khalti', 'fonepay', 'pay_later'] as const;

export default function BusTicketsPage() {
  const { user, profile } = useAuth();
  const [from, setFrom] = useState('Kathmandu');
  const [to, setTo] = useState('Pokhara');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [passengers, setPassengers] = useState(1);
  const [busClass, setBusClass] = useState('all');
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState<BusTrip[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<BusTrip | null>(null);
  const [showBooking, setShowBooking] = useState(false);

  // Booking form
  const [fullName, setFullName] = useState(profile?.full_name || '');
  const [email, setEmail] = useState(profile?.email || '');
  const [phone, setPhone] = useState(profile?.phone || '');
  const [paymentMethod, setPaymentMethod] = useState<'esewa' | 'khalti' | 'fonepay' | 'pay_later'>('esewa');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookingRef, setBookingRef] = useState('');
  const [bookingError, setBookingError] = useState('');

  const today = new Date().toISOString().split('T')[0];

  const handleSearch = useCallback(() => {
    if (!from || !to || from === to) return;
    setLoading(true);
    setSearched(true);
    setSelectedTrip(null);
    setShowBooking(false);
    setTimeout(() => {
      const trips = generateDemoBusTrips(from, to, date, passengers);
      const filtered = busClass === 'all' ? trips : trips.filter(t => t.busType.toLowerCase().includes(busClass));
      setResults(filtered);
      setLoading(false);
    }, 600);
  }, [from, to, date, passengers, busClass]);

  const handleSelectTrip = (trip: BusTrip) => {
    if (!user) { window.location.href = `/login?redirect=/bus-tickets`; return; }
    setSelectedTrip(trip);
    setFullName(profile?.full_name || '');
    setEmail(profile?.email || '');
    setPhone(profile?.phone || '');
    setShowBooking(true);
    setSubmitted(false);
    setBookingError('');
  };

  const handleBookSubmit = async () => {
    if (!selectedTrip) return;
    setBookingError('');
    if (!fullName.trim()) { setBookingError('Full name is required.'); return; }
    if (!email.trim()) { setBookingError('Email is required.'); return; }
    setSubmitting(true);
    try {
      const ref = generateBookingRef('bus');
      const supabase = createClient();
      const payload = {
        tourist_id: user?.id || null,
        service_type: 'bus',
        service_id: selectedTrip.id,
        booking_reference: ref,
        booking_name: `Bus: ${selectedTrip.from} → ${selectedTrip.to}`,
        full_name: fullName,
        email,
        phone,
        start_date: date,
        travelers: passengers,
        origin: selectedTrip.from,
        destination: selectedTrip.to,
        base_amount: selectedTrip.baseFare,
        tax: selectedTrip.tax,
        service_fee: selectedTrip.serviceFee,
        total_price: selectedTrip.totalFare,
        currency: 'NPR',
        booking_status: 'pending',
        payment_status: 'unpaid',
        booking_type: 'bus',
        is_demo: true,
        price_snapshot: JSON.stringify({ operator: selectedTrip.operator, busType: selectedTrip.busType, departure: selectedTrip.departureISO, arrival: selectedTrip.arrivalISO }),
      };
      await supabase.from('bookings').insert(payload);
      setBookingRef(ref);
      setSubmitted(true);
    } catch (err: any) {
      setBookingError('Booking failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-navy">
      <Header />

      <div className="pt-24 pb-8 px-4" style={{ background: 'linear-gradient(180deg, #0E3570 0%, #0B2D5B 100%)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="section-eyebrow mb-2">Nepal Bus Network</div>
          <h1 className="heading-display text-3xl md:text-4xl text-snow mb-2">
            Bus <span className="italic text-gold">Tickets</span>
          </h1>
          <p className="text-snow/50 text-sm">Search tourist buses, deluxe coaches and local services across Nepal.</p>
          {IS_SANDBOX && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs" style={{ background: 'rgba(245,192,48,0.1)', border: '1px solid rgba(245,192,48,0.2)', color: 'rgba(245,192,48,0.8)' }}>
              ⚠️ Demo Mode — Schedules are estimated. No real tickets issued.
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Search form */}
        <div className="yn-card rounded-2xl p-6 mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-1.5 block">From</label>
              <select value={from} onChange={e => setFrom(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-snow focus:outline-none focus:border-gold/40">
                {NEPAL_CITIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-1.5 block">To</label>
              <select value={to} onChange={e => setTo(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-snow focus:outline-none focus:border-gold/40">
                {NEPAL_CITIES.filter(c => c !== from).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Date</label>
              <input type="date" value={date} min={today} onChange={e => setDate(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-snow focus:outline-none focus:border-gold/40" />
            </div>
            <div>
              <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Passengers</label>
              <select value={passengers} onChange={e => setPassengers(Number(e.target.value))}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-snow focus:outline-none focus:border-gold/40">
                {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={handleSearch} disabled={from === to}
                className="w-full py-3 bg-gold text-navy font-bold rounded-xl hover:bg-gold/90 transition-colors disabled:opacity-40">
                🔍 Search
              </button>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            {['all', 'tourist', 'deluxe', 'local'].map(c => (
              <button key={c} onClick={() => setBusClass(c)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${busClass === c ? 'bg-gold text-navy border-gold font-semibold' : 'border-white/15 text-snow/60 hover:border-gold/40'}`}>
                {c === 'all' ? 'All Classes' : c.charAt(0).toUpperCase() + c.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        {loading && (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-32 bg-white/5 rounded-2xl animate-pulse" />)}
          </div>
        )}

        {!loading && searched && results.length === 0 && (
          <div className="text-center py-16">
            <div className="text-4xl mb-3">🚌</div>
            <h3 className="text-snow font-semibold mb-2">No buses found</h3>
            <p className="text-snow/40 text-sm">Try different dates or route</p>
          </div>
        )}

        {!loading && results.length > 0 && !showBooking && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-snow font-bold">{results.length} buses found · {from} → {to}</h2>
              <span className="text-snow/40 text-xs">{date} · {passengers} passenger{passengers > 1 ? 's' : ''}</span>
            </div>
            {results.map(trip => (
              <div key={trip.id} className="yn-card rounded-2xl overflow-hidden hover:border-gold/30 transition-all">
                <div className="px-4 py-1.5 text-[10px]" style={{ background: 'rgba(245,192,48,0.08)', borderBottom: '1px solid rgba(245,192,48,0.15)', color: 'rgba(245,192,48,0.7)' }}>
                  {trip.demoLabel}
                </div>
                <div className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">🚌</span>
                        <div>
                          <div className="text-snow font-bold text-sm">{trip.operator}</div>
                          <div className="text-snow/40 text-xs">{trip.busType} · {trip.busNumber}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div>
                          <div className="text-snow font-bold text-xl tabular-nums">{formatTime(trip.departureISO)}</div>
                          <div className="text-snow/50 text-xs">{trip.from}</div>
                          <div className="text-snow/30 text-[10px]">{formatDate(trip.departureISO)}</div>
                        </div>
                        <div className="flex flex-col items-center flex-1">
                          <div className="text-snow/40 text-xs mb-1">{formatDuration(trip.durationMinutes)}</div>
                          <div className="w-full h-px bg-white/20" />
                          <div className="text-snow/40 text-[10px] mt-1">Direct</div>
                        </div>
                        <div className="text-right">
                          <div className="text-snow font-bold text-xl tabular-nums">
                            {formatTime(trip.arrivalISO)}
                            {isNextDay(trip.departureISO, trip.arrivalISO) && (
                              <span className="text-xs text-amber-400 ml-1">+1 day</span>
                            )}
                          </div>
                          <div className="text-snow/50 text-xs">{trip.to}</div>
                          <div className="text-snow/30 text-[10px]">{formatDate(trip.arrivalISO)}</div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-3">
                        {trip.amenities.map(a => (
                          <span key={a} className="text-[10px] px-2 py-0.5 bg-white/5 text-snow/50 rounded-full">{a}</span>
                        ))}
                        <span className="text-[10px] px-2 py-0.5 bg-sky/10 text-sky rounded-full">{trip.availableSeats} seats left</span>
                      </div>
                    </div>
                    <div className="sm:text-right sm:min-w-[140px]">
                      <div className="text-snow/40 text-xs mb-1">Total ({passengers} pax)</div>
                      <div className="text-gold font-bold text-xl">NPR {trip.totalFare.toLocaleString()}</div>
                      <div className="text-snow/30 text-[10px] mb-3">incl. tax & fees</div>
                      <div className="flex gap-2 sm:flex-col">
                        <button onClick={() => handleSelectTrip(trip)}
                          className="flex-1 sm:flex-none px-4 py-2.5 bg-gold text-navy font-bold rounded-xl text-sm hover:bg-gold/90 transition-colors">
                          Quick Book
                        </button>
                        <Link href={`/bus-tickets/${trip.id}/book`}
                          className="flex-1 sm:flex-none px-4 py-2.5 text-center rounded-xl text-sm font-semibold transition-colors"
                          style={{ background: 'rgba(245,192,48,0.12)', color: 'var(--gold)', border: '1px solid rgba(245,192,48,0.3)' }}>
                          Full Form →
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Booking panel */}
        {showBooking && selectedTrip && !submitted && (
          <div className="max-w-2xl mx-auto">
            <button onClick={() => setShowBooking(false)} className="text-snow/40 text-sm hover:text-snow/70 mb-4 inline-flex items-center gap-1">← Back to results</button>
            <div className="yn-card rounded-2xl p-6 space-y-5">
              <h2 className="text-snow font-bold text-lg">Complete Booking</h2>

              {/* Trip summary */}
              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-snow font-semibold text-sm">{selectedTrip.operator}</div>
                  <div className="text-snow/40 text-xs">{selectedTrip.busType}</div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <div>
                    <div className="text-snow font-bold">{formatTime(selectedTrip.departureISO)}</div>
                    <div className="text-snow/50 text-xs">{selectedTrip.from}</div>
                  </div>
                  <div className="flex-1 text-center text-snow/30 text-xs">→ {formatDuration(selectedTrip.durationMinutes)}</div>
                  <div className="text-right">
                    <div className="text-snow font-bold">
                      {formatTime(selectedTrip.arrivalISO)}
                      {isNextDay(selectedTrip.departureISO, selectedTrip.arrivalISO) && <span className="text-amber-400 text-xs ml-1">+1 day</span>}
                    </div>
                    <div className="text-snow/50 text-xs">{selectedTrip.to}</div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-white/10 space-y-1 text-xs">
                  <div className="flex justify-between text-snow/50"><span>Base fare ({passengers} pax)</span><span>NPR {selectedTrip.baseFare.toLocaleString()}</span></div>
                  <div className="flex justify-between text-snow/50"><span>Tax (13%)</span><span>NPR {selectedTrip.tax.toLocaleString()}</span></div>
                  <div className="flex justify-between text-snow/50"><span>Service fee (2%)</span><span>NPR {selectedTrip.serviceFee.toLocaleString()}</span></div>
                  <div className="flex justify-between font-bold text-sm pt-1 border-t border-white/10">
                    <span className="text-snow">Total</span><span className="text-gold">NPR {selectedTrip.totalFare.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="bg-amber-400/10 border border-amber-400/20 rounded-xl p-3 text-xs text-amber-400/80">
                ⚠️ Demo Mode: This is an estimated schedule. No real ticket will be issued. Use &quot;Request Booking Assistance&quot; for actual bookings.
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Full Name *</label>
                  <input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your full name"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-snow placeholder-snow/30 focus:outline-none focus:border-gold/40" />
                </div>
                <div>
                  <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Email *</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-snow placeholder-snow/30 focus:outline-none focus:border-gold/40" />
                </div>
                <div>
                  <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Phone</label>
                  <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+977 98XXXXXXXX"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-snow placeholder-snow/30 focus:outline-none focus:border-gold/40" />
                </div>
              </div>

              <div>
                <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-2 block">Payment Method</label>
                <div className="grid grid-cols-2 gap-2">
                  {PAYMENT_OPTIONS.map(pm => {
                    const info = PAYMENT_PROVIDER_INFO[pm];
                    return (
                      <button key={pm} type="button" onClick={() => setPaymentMethod(pm)}
                        className={`p-3 rounded-xl border text-left transition-all ${paymentMethod === pm ? 'border-gold/50 bg-gold/5' : `border-white/10 ${info.color} hover:border-white/20`}`}>
                        <div className="flex items-center gap-2">
                          <span>{info.icon}</span>
                          <span className="text-snow text-xs font-semibold">{info.label}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <p className="text-amber-400/60 text-[10px] mt-2">⚠️ {SANDBOX_WARNING.en}</p>
              </div>

              {bookingError && <p className="text-red-400 text-sm bg-red-400/10 rounded-xl px-4 py-3">{bookingError}</p>}

              <div className="flex gap-3">
                <button onClick={() => setShowBooking(false)} className="flex-1 py-3 border border-white/20 text-snow/70 rounded-xl hover:border-white/40 transition-colors">Cancel</button>
                <button onClick={handleBookSubmit} disabled={submitting}
                  className="flex-1 py-3 bg-gold text-navy font-bold rounded-xl hover:bg-gold/90 transition-colors disabled:opacity-50">
                  {submitting ? 'Processing...' : 'Request Booking Assistance'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success */}
        {submitted && selectedTrip && (
          <div className="max-w-2xl mx-auto text-center py-10">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-snow text-2xl font-bold mb-2">Booking Request Received!</h2>
            <p className="text-snow/60 text-sm mb-1">{selectedTrip.from} → {selectedTrip.to} on {date}</p>
            <div className="yn-card rounded-2xl p-4 my-6 text-left max-w-sm mx-auto">
              <div className="text-xs text-snow/40 mb-1">Reference</div>
              <div className="text-gold font-bold font-mono text-sm">{bookingRef}</div>
              <div className="text-xs text-snow/40 mt-3 mb-1">Total</div>
              <div className="text-gold font-bold">NPR {selectedTrip.totalFare.toLocaleString()}</div>
              <div className="text-xs text-amber-400/70 mt-3">⚠️ Demo mode — no real ticket issued. Our team will contact you to confirm.</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link href="/booking/my-bookings" className="btn-gold px-6 py-3 rounded-xl text-sm font-semibold">My Bookings</Link>
              <button onClick={() => { setSubmitted(false); setShowBooking(false); setSelectedTrip(null); }} className="btn-outline-gold px-6 py-3 rounded-xl text-sm font-semibold">Search Again</button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
}
